import {
  collection,
  deleteDoc,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase/config";

/**
 * Legge tutte le richieste di accesso (collection "accessRequests") ordinate
 * per data di creazione (più recenti prima). Richiede regola Firestore
 * `allow read: if isAdmin()` (già presente).
 */
export async function fetchAccessRequests() {
  const q = query(collection(db, "accessRequests"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      nome: data.nome || "",
      email: data.email || "",
      professione: data.professione || "",
      // Compatibilità: la callable server-side salva "anniPiva"/"strumenti",
      // un percorso client legacy salvava "anniAttivita"/"strumentiUsati".
      anniAttivita: data.anniAttivita || data.anniPiva || "",
      fonte: data.fonte || "",
      strumentiUsati: data.strumentiUsati || data.strumenti || "",
      status: data.status || "new",
      createdAtMs: data.createdAt?.toDate ? data.createdAt.toDate().getTime() : null,
    };
  });
}

/**
 * Aggiorna lo status di una richiesta (approved/rejected).
 * Le Firestore rules consentono già questo update SOLO agli admin e SOLO sui
 * campi `status` (limitato a 'approved'/'rejected') e `reviewedAt`:
 *   allow update: if isAdmin()
 *                 && request.resource.data.status in ['approved', 'rejected']
 *                 && request.resource.data.diff(resource.data)
 *                     .affectedKeys().hasOnly(['status', 'reviewedAt']);
 * Non modificare qui altri campi, altrimenti la scrittura verrà rifiutata.
 */
export async function updateAccessRequestStatus(requestId, status, email) {
  const ref = doc(db, "accessRequests", requestId);
  await updateDoc(ref, {
    status,
    reviewedAt: serverTimestamp(),
  });

  // Approvare deve APRIRE la porta, non solo mandare un'email: senza questo
  // documento l'utente resterebbe nella schermata di attesa dopo aver ricevuto
  // il messaggio "sei stato approvato".
  const chiave = (email || "").trim().toLowerCase();
  if (status === "approved" && chiave) {
    await setDoc(doc(db, "approvedUsers", chiave), {
      email: chiave,
      source: "request",
      requestId,
      approvedAt: serverTimestamp(),
    });
  }
}

/** Elenco degli accessi approvati, per la sezione admin. */
export async function fetchApprovedUsers() {
  const snap = await getDocs(query(collection(db, "approvedUsers")));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

/** Concede l'accesso a un indirizzo, anche se non si è ancora registrato. */
export async function grantApproval(email) {
  const chiave = (email || "").trim().toLowerCase();
  if (!chiave) throw new Error("Email mancante.");
  await setDoc(doc(db, "approvedUsers", chiave), {
    email: chiave,
    source: "manual",
    approvedAt: serverTimestamp(),
  });
}

/** Revoca l'accesso. L'utente torna alla schermata di attesa. */
export async function revokeApproval(email) {
  await deleteDoc(doc(db, "approvedUsers", (email || "").trim().toLowerCase()));
}
